import { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, CheckCircle, AlertCircle, Droplets, Sun, Shield } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';

const HealingTrackerPage = () => {
  const { getContent } = useSiteContent();
  const [currentDay, setCurrentDay] = useState(1);

  const healingStages = [
    {
      days: '1-3',
      title: 'Initial Healing',
      description: 'Your tattoo will be red, swollen, and may ooze clear fluid. This is normal!',
      dos: [
        'Keep it clean and moisturized',
        'Wash 2-3 times daily with antibacterial soap',
        'Apply thin layer of ointment',
        'Wear loose, breathable clothing'
      ],
      donts: [
        'Don\'t pick at scabs',
        'Don\'t soak in water (no baths, pools, hot tubs)',
        'Don\'t expose to direct sunlight',
        'Don\'t wear tight clothing over tattoo'
      ],
      icon: <Droplets size={48} />
    },
    {
      days: '4-7',
      title: 'Peeling Stage',
      description: 'Your tattoo will start to peel like a sunburn. Colors may look dull - this is temporary!',
      dos: [
        'Continue washing gently',
        'Keep it moisturized',
        'Let skin peel naturally',
        'Stay hydrated'
      ],
      donts: [
        'Don\'t pick or scratch',
        'Don\'t over-moisturize',
        'Don\'t panic about color loss',
        'Don\'t use scented lotions'
      ],
      icon: <Shield size={48} />
    },
    {
      days: '8-14',
      title: 'Surface Healing',
      description: 'Most of the peeling is done. Your tattoo should look mostly healed on the surface.',
      dos: [
        'Continue moisturizing',
        'Protect from sun with SPF 50+',
        'Maintain good hygiene',
        'Be patient with color return'
      ],
      donts: [
        'Don\'t assume you\'re fully healed',
        'Don\'t skip moisturizer',
        'Don\'t sunbathe',
        'Don\'t scratch if itchy'
      ],
      icon: <Sun size={48} />
    },
    {
      days: '15-30',
      title: 'Complete Healing',
      description: 'Your tattoo is fully healed! Colors should be vibrant and skin texture back to normal.',
      dos: [
        'Always use sunscreen on your tattoo',
        'Keep skin moisturized',
        'Schedule touch-up if needed',
        'Enjoy your beautiful art!'
      ],
      donts: [
        'Don\'t forget sun protection',
        'Don\'t neglect moisturizing',
        'Don\'t expose to harsh chemicals',
        'Don\'t ignore any concerns'
      ],
      icon: <CheckCircle size={48} />
    },
  ];

  const redFlags = [
    'Excessive redness or swelling that gets worse after 3 days',
    'Pus or yellow/green discharge',
    'Fever or chills',
    'Excessive heat around the tattoo',
    'Red streaks extending from the tattoo',
    'Severe pain that increases over time'
  ];

  return (
    <div data-testid="healing-tracker-page" className="tattoo-bg-healing min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        
        {/* Hero Section */}
        <section className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-7xl md:text-8xl font-bold mb-6 leading-none whitespace-pre-line">
              {getContent('healing_tracker_title', 'HEALING\nTRACKER')}
            </h1>
            <p className="font-body text-xl max-w-2xl leading-relaxed mb-8">
              {getContent('healing_tracker_subtitle', 'Your tattoo healing journey, day by day. Follow these guidelines for optimal results.')}
            </p>
            
            <div className="paper-texture p-8 max-w-md">
              <label className="font-heading text-xl mb-4 block">Which day are you on?</label>
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => setCurrentDay(prev => Math.max(1, prev - 1))}
                  className="w-14 h-14 border-2 border-black text-2xl font-bold hover:bg-black hover:text-white transition-colors"
                  data-testid="day-decrease"
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  max="30"
                  value={currentDay}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setCurrentDay(1);
                    } else {
                      const num = parseInt(val, 10);
                      if (!isNaN(num)) {
                        setCurrentDay(Math.max(1, Math.min(30, num)));
                      }
                    }
                  }}
                  className="w-24 border-2 border-black px-4 py-4 text-3xl font-bold text-center focus:outline-none focus:ring-2 focus:ring-black"
                  data-testid="day-input"
                />
                <button
                  onClick={() => setCurrentDay(prev => Math.min(30, prev + 1))}
                  className="w-14 h-14 border-2 border-black text-2xl font-bold hover:bg-black hover:text-white transition-colors"
                  data-testid="day-increase"
                >
                  +
                </button>
              </div>
              <p className="font-mono text-xs uppercase tracking-widest text-gray-600 mt-4 text-center">
                Day {currentDay} of 30
              </p>
            </div>
          </motion.div>
        </section>

        {/* Healing Stages */}
        <section className="mb-20">
          <div className="border-t-2 border-black pt-8 mb-12">
            <h2 className="font-heading text-5xl font-bold">HEALING STAGES</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {healingStages.map((stage, index) => {
              const [minDay, maxDay] = stage.days.split('-').map(d => parseInt(d));
              const isCurrentStage = currentDay >= minDay && currentDay <= maxDay;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`paper-texture p-8 border-2 transition-all ${
                    isCurrentStage ? 'border-black shadow-lg' : 'border-gray-300'
                  }`}
                  data-testid={`stage-${index + 1}`}
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0">{stage.icon}</div>
                    <div>
                      <p className="font-mono text-xs uppercase tracking-widest text-gray-600 mb-2">
                        Days {stage.days}
                        {isCurrentStage && <span className="ml-2 bg-black text-white px-2 py-1">YOU ARE HERE</span>}
                      </p>
                      <h3 className="font-heading text-3xl font-bold mb-2">{stage.title}</h3>
                      <p className="font-body text-base leading-relaxed text-gray-700">{stage.description}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <div>
                      <h4 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
                        <CheckCircle size={20} className="text-green-600" />
                        DO
                      </h4>
                      <ul className="space-y-2">
                        {stage.dos.map((item, i) => (
                          <li key={i} className="font-body text-sm flex items-start gap-2">
                            <span className="text-green-600 mt-1">✓</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
                        <AlertCircle size={20} className="text-red-600" />
                        DON'T
                      </h4>
                      <ul className="space-y-2">
                        {stage.donts.map((item, i) => (
                          <li key={i} className="font-body text-sm flex items-start gap-2">
                            <span className="text-red-600 mt-1">✗</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Red Flags Section */}
        <section className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="border-2 border-red-600 bg-red-50 p-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <AlertCircle size={48} className="text-red-600 flex-shrink-0" />
              <div>
                <h2 className="font-heading text-4xl font-bold mb-2 text-red-600">RED FLAGS</h2>
                <p className="font-body text-lg">If you experience any of these symptoms, contact us or see a doctor immediately:</p>
              </div>
            </div>
            <ul className="space-y-3">
              {redFlags.map((flag, index) => (
                <li key={index} className="font-body text-base flex items-start gap-3">
                  <span className="text-red-600 text-xl">⚠</span>
                  {flag}
                </li>
              ))}
            </ul>
          </motion.div>
        </section>

        {/* Aftercare Products */}
        <section>
          <div className="border-t-2 border-black pt-8 mb-12">
            <h2 className="font-heading text-5xl font-bold">RECOMMENDED PRODUCTS</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="paper-texture p-8"
            >
              <h3 className="font-heading text-2xl font-bold mb-3">CLEANSER</h3>
              <p className="font-body text-base text-gray-700 mb-4">
                Antibacterial soap (fragrance-free)
              </p>
              <p className="font-body text-sm text-gray-600">
                Use 2-3 times daily during healing
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="paper-texture p-8"
            >
              <h3 className="font-heading text-2xl font-bold mb-3">MOISTURIZER</h3>
              <p className="font-body text-base text-gray-700 mb-4">
                Fragrance-free, hypoallergenic lotion
              </p>
              <p className="font-body text-sm text-gray-600">
                Apply thin layer 2-3 times daily
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="paper-texture p-8"
            >
              <h3 className="font-heading text-2xl font-bold mb-3">SUNSCREEN</h3>
              <p className="font-body text-base text-gray-700 mb-4">
                SPF 50+ broad spectrum
              </p>
              <p className="font-body text-sm text-gray-600">
                Essential after healing is complete
              </p>
            </motion.div>
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default HealingTrackerPage;